import type { BaseQueryFn } from '@reduxjs/toolkit/query';

import chunk from 'lodash/chunk';

import { endpoints as autogenEnhancements } from '~/api/unified-fields/enhancements.autogen';
import { Tags as AutogenTags } from '~/api/unified-fields/tags.autogen';

import type { UnifiedFieldsBaseQueryArg } from './baseQuery';

import { TagType } from '../TagType';
import { unifiedFieldsApiRoot } from './root';

// Apply schema-derived providesTags / invalidatesTags for all annotated endpoints
unifiedFieldsApiRoot.enhanceEndpoints({ endpoints: autogenEnhancements });

// UUIDs are ~36 chars each; 40 IDs ≈ 1.4 kB of query string, keeping well under the ~2 kB URL length limit.
const BATCH_SIZE = 40;

// The client_fields endpoint's page[limit] cap: raised from 100 to 1000
// server-side, so a tenant's full field set (well under 1000 for any real
// client today) fits in a single page instead of ~10.
const FIELDS_PAGE_LIMIT = 1000;

// Every query that returns client fields needs to refetch after a field
// section move/reorder, not just after a field itself is created/updated —
// those endpoints are codegen-only (never redefined here), so they invalidate
// under the codegen tag namespace (`AutogenTags`) rather than `TagType`. Both
// tags are provided so either invalidation source triggers a refetch.
const clientFieldListTags = [
  { type: TagType.UnifiedField, id: 'LIST' },
  { type: AutogenTags.ClientField, id: 'LIST' },
] as const;

// --- Enums ---

export type LogicalType =
  | 'TEXT'
  | 'NUMBER'
  | 'BOOLEAN'
  | 'DATE'
  | 'MONETARY'
  | 'FILE'
  | 'REFERENCE'
  | 'USER'
  | 'EMAIL'
  | 'URL'
  | 'DEPARTMENT'
  | 'ADDRESS'
  | 'DURATION';
type SearchType =
  | 'KEYWORD'
  | 'TEXT'
  | 'NUMBER'
  | 'DATE'
  | 'BOOLEAN'
  | 'ATTACHMENT'
  | 'REFERENCE'
  | 'USER'
  | 'DEPARTMENT';
type DataClassification = 'PUBLIC' | 'INTERNAL' | 'PII' | 'CONFIDENTIAL';
type ValidationContext = 'user_input' | 'system';

// --- Shared resource types ---

type IntegrationFieldMapping = {
  id: string;
  namespace: string;
  integration_key: string;
  domain?: string | null;
};

export type ClientField = {
  type: string;
  id: string;
  attributes: {
    custom_name: string;
    description?: string | null;
    api_name: string | null;
    logical_type: LogicalType;
    search_type: SearchType;
    deactivated_at: string | null;
    option_set_id: string | null;
    supports_option_set: boolean;
    default_value: Record<string, unknown> | null;
    custom_settings: {
      allow_multiple_values?: boolean;
      allow_adding_new_options?: boolean;
      reference_entity?: string;
      [key: string]: unknown;
    };
    display_settings: Record<string, unknown>;
    suggestions_id: string | null;
    suggestion_source_config?: { type: string } | null;
    data_classification: DataClassification;
    created_by: number | null;
    updated_by: number | null;
    created_at: string | null;
    updated_at: string | null;
    integration_field_mappings: IntegrationFieldMapping[];
    is_editable: boolean;
    referenced_by_field_lookup_tables?: string[];
    /** The field section this field currently belongs to, if any. */
    field_section_id?: string | null;
    /** The field's order within its field section, if it belongs to one. */
    field_section_position?: number | null;
    doc_restriction?: 'EXCLUDE' | 'INCLUDE' | null;
    doc_types?: string[];
  };
  meta?: Record<string, unknown> | null;
};

// A field section, embedded via `include=field_section` on the fields it
// belongs to.
type FieldSection = {
  type: string;
  id: string;
  attributes: {
    name: string;
    position: number;
  };
};

// --- listFields ---

export type ListFieldsApiResponse = {
  data: ClientField[];
  included?: FieldSection[] | null;
  meta: {
    page: {
      offset: number;
      limit: number;
      total: number;
    };
  };
};

type ListFieldsApiArg = {
  'page[offset]'?: number;
  'page[limit]'?: number;
  'filter[id]'?: string | null;
  'filter[api_name]'?: string | null;
  'filter[active]'?: boolean | null;
  'filter[search_query]'?: string;
  sort?: string;
  /** Pass 'field_section' to embed each field's section in the response's `included`. */
  include?: string;
};

// --- createFields ---

type CreateFieldAttributes = {
  custom_name: string;
  description?: string;
  logical_type: LogicalType;
  search_type?: SearchType;
  data_classification: DataClassification;
  option_set_id?: string | null;
  default_value?: Record<string, unknown> | null;
  custom_settings?: Record<string, unknown> | null;
  display_settings?: Record<string, unknown> | null;
  suggestions_id?: string | null;
  integration_field_mappings?:
    | { namespace: string; integration_key: string }[]
    | null;
  doc_restriction?: 'EXCLUDE' | 'INCLUDE' | null;
  doc_types?: string[];
};

type CreateFieldsApiArg = {
  data: { type: 'client_fields'; attributes: CreateFieldAttributes }[];
};

type CreateFieldsApiResponse = ListFieldsApiResponse;

// --- updateField ---

type UpdateFieldAttributes = {
  custom_name?: string | null;
  description?: string | null;
  data_classification?: DataClassification | null;
  is_active?: boolean | null;
  option_set_id?: string | null;
  default_value?: Record<string, unknown> | null;
  custom_settings?: Record<string, unknown> | null;
  display_settings?: Record<string, unknown> | null;
  suggestions_id?: string | null;
  doc_restriction?: 'EXCLUDE' | 'INCLUDE' | null;
  doc_types?: string[];
};

type UpdateFieldApiArg = {
  field_id: string;
  data: {
    type: 'client_fields';
    attributes: UpdateFieldAttributes;
    meta: { version: number };
  };
};

type UpdateFieldApiResponse = {
  data: ClientField;
};

// --- option sets ---

// USER/DEPARTMENT values come from Pilot endpoints, not a field option set;
// FILE is a composite/id-backed type with no option set either.
export type OptionSetLogicalType = Exclude<
  LogicalType,
  'FILE' | 'USER' | 'DEPARTMENT'
>;

type OptionSet = {
  type: string;
  id: string;
  attributes: {
    logical_type: OptionSetLogicalType;
  };
};

type CreateOptionSetApiArg = {
  data: {
    type: 'option_sets';
    attributes: { logical_type: OptionSetLogicalType };
  };
};

type CreateOptionSetApiResponse = {
  data: OptionSet;
};

type DeleteOptionSetApiArg = {
  option_set_id: string;
};

// --- field options ---

// Option value, e.g. { value } for scalars or { amount, currency } for monetary.
export type FieldOptionValue = {
  value?: unknown;
  [key: string]: unknown;
};

export type FieldOption = {
  type: string;
  id: string;
  attributes: {
    option_set_id: string;
    value: FieldOptionValue;
    sort_value?: number | null;
    /** Per-type human form materialized server-side; null on legacy rows written before the column existed. */
    display_text?: string | null;
    /** Present (`true`) only for the option set's single default; omitted for every other option. */
    default?: boolean | null;
  };
};

type ListFieldOptionsApiArg = {
  'page[offset]'?: number;
  'page[limit]'?: number;
  'filter[option_set_id]'?: string;
  'filter[search]'?: string;
};

type ListFieldOptionsApiResponse = {
  data: FieldOption[];
  meta: {
    page: {
      offset: number;
      limit: number;
      total: number;
    };
  };
};

type CreateFieldOptionItem = {
  type: 'field_options';
  attributes: {
    option_set_id: string;
    value: FieldOptionValue;
    /** Marks this item as the set's default, demoting any prior default. At most one item per batch may set it. */
    default?: boolean;
  };
};

type SkippedFieldOption = {
  index: number;
  reason: 'duplicate' | 'invalid';
  value: FieldOptionValue;
  detail?: string;
};

export type CreateFieldOptionsApiArg = {
  data: CreateFieldOptionItem[];
  /** Skips cache invalidation for this call. Used by chunked imports so only the final chunk triggers a refetch. */
  skipInvalidation?: boolean;
};

export type CreateFieldOptionsApiResponse = {
  data: FieldOption[];
  meta: { skipped: SkippedFieldOption[] };
};

type DeleteFieldOptionsApiArg = {
  ids: string[];
  option_set_id: string;
};

type UpdateFieldOptionAttributes = {
  value?: FieldOptionValue | null;
  /** `true` promotes this option to its set's default (demoting any prior default); `false` clears it. */
  default?: boolean | null;
};

type UpdateFieldOptionApiArg = {
  option_id: string;
  /** Not sent on the wire — used only to invalidate the right option-set cache entry. */
  option_set_id: string;
  data: {
    type: 'field_options';
    attributes: UpdateFieldOptionAttributes;
  };
};

type UpdateFieldOptionApiResponse = {
  data: FieldOption;
};

// --- validateFields ---

type JsonApiError = {
  status: string;
  code: string;
  detail: string;
  source?: { pointer?: string | null; parameter?: string | null } | null;
};

type ValidateFieldsApiArg = {
  context?: ValidationContext;
  items: { field_id: string; value: unknown }[];
};

type ValidateFieldsApiResponse = {
  results: {
    field_id: string;
    valid: boolean;
    errors?: JsonApiError[] | null;
  }[];
};

// --- Field Lookup Tables ---

export type FieldLookupTable = {
  type: string;
  id: string;
  attributes: {
    name: string;
    description: string | null;
    column_count: number | null;
    row_count: number | null;
    created_by: number | null;
    updated_by: number | null;
    created_at: string | null;
    updated_at: string | null;
    deleted_at: string | null;
  };
  meta?: { version: number } | null;
};

type ListLookupTablesApiArg = {
  'page[offset]'?: number;
  'page[limit]'?: number;
  'filter[id]'?: string;
  'filter[active]'?: boolean | null;
  'filter[name]'?: string;
  'filter[reference_field_id]'?: string;
  include_columns?: boolean;
};

type ListLookupTablesApiResponse = {
  data: FieldLookupTable[];
  included?: FieldLookupTableColumn[] | null;
  meta: {
    page: { offset: number; limit: number; total: number };
  };
};

type GetLookupTableApiArg = { table_id: string };

type GetLookupTableApiResponse = { data: FieldLookupTable };

type CreateLookupTableApiArg = {
  data: {
    type: 'field_lookup_tables';
    attributes: { name: string; description?: string | null };
  };
};

type CreateLookupTableApiResponse = { data: FieldLookupTable };

type UpdateLookupTableApiArg = {
  table_id: string;
  data: {
    type: 'field_lookup_tables';
    attributes: { name?: string | null; description?: string | null };
    meta: { version: number };
  };
};

type UpdateLookupTableApiResponse = { data: FieldLookupTable };

type DeleteLookupTableApiArg = { table_id: string };

// --- Field Lookup Table Columns ---

type ClientFieldIncluded = {
  type: string;
  id: string;
  attributes: {
    custom_name: string;
    api_name: string | null;
    logical_type: LogicalType;
    option_set_id: string | null;
    data_classification: DataClassification;
    deactivated_at: string | null;
    created_at: string | null;
    updated_at: string | null;
  };
};

type FieldOptionIncluded = {
  type: string;
  id: string;
  attributes: {
    option_set_id: string;
    value: FieldOptionValue;
    display_text: string | null;
    sort_value: string | null;
    created_at: string | null;
    updated_at: string | null;
  };
};

export type FieldLookupTableColumn = {
  type: string;
  id: string;
  attributes: {
    key: string;
    position: number;
    client_field_id: string;
    option_set_id: string | null;
    label: string | null;
    logical_type: string | null;
    is_active: boolean | null;
    is_reference_column: boolean;
    required: boolean;
    default_value: Record<string, unknown> | null;
    display_settings: Record<string, unknown>;
    created_by: number | null;
    updated_by: number | null;
    created_at: string | null;
    updated_at: string | null;
    deleted_at: string | null;
  };
  meta?: { version: number } | null;
};

type ListLookupTableColumnsApiArg = {
  'filter[column_id]'?: string;
  'filter[field_lookup_table_id]'?: string;
  'page[offset]'?: number;
  'page[limit]'?: number;
  include_client_field?: boolean;
};

type ListLookupTableColumnsApiResponse = {
  data: FieldLookupTableColumn[];
  included?: (ClientFieldIncluded | FieldOptionIncluded)[] | null;
  meta: {
    page: { offset: number; limit: number; total: number };
  };
};

type GetLookupTableColumnApiArg = {
  column_id: string;
  include_client_field?: boolean;
};

type GetLookupTableColumnApiResponse = {
  data: FieldLookupTableColumn;
  included?: (ClientFieldIncluded | FieldOptionIncluded)[] | null;
};

type CreateLookupTableColumnItem = {
  type: 'field_lookup_table_columns';
  attributes: {
    key: string;
    client_field_id: string;
    required?: boolean;
    default_value?: Record<string, unknown> | null;
    display_settings?: Record<string, unknown> | null;
    is_reference_column?: boolean;
  };
};

type CreateLookupTableColumnsApiArg = {
  meta: { field_lookup_table_id: string };
  data: CreateLookupTableColumnItem[];
};

type CreateLookupTableColumnsApiResponse = ListLookupTableColumnsApiResponse;

type UpdateLookupTableColumnApiArg = {
  column_id: string;
  data: {
    type: 'field_lookup_table_columns';
    attributes: {
      required?: boolean | null;
      default_value?: Record<string, unknown> | null;
      display_settings?: Record<string, unknown> | null;
      is_reference_column?: boolean | null;
    };
    meta: { version: number };
  };
};

type UpdateLookupTableColumnApiResponse = {
  data: FieldLookupTableColumn;
  included?: (ClientFieldIncluded | FieldOptionIncluded)[] | null;
};

type DeleteLookupTableColumnApiArg = { column_id: string };

type ReorderLookupTableColumnsApiArg = {
  field_lookup_table_id: string;
  data:
    | {
        type: 'field_lookup_table_column_moves';
        attributes: { column_id: string; before_column_id?: string | null };
        meta: { version: number };
      }
    | {
        type: 'field_lookup_table_column_orders';
        attributes: { ordered_column_ids: string[] };
        meta: { version: number };
      };
};

// --- Field Lookup Table Rows ---

// The full Unified Field value container a lookup table cell reads/writes,
// e.g. { data: [{ value: 'NA' }] }, or multiple entries when the backing
// field allows multiple values.
export type FieldValueContainer = { data: FieldOptionValue[] };

export type FieldLookupTableCell = {
  type: string;
  id: string;
  attributes: {
    column_key: string | null;
    column_id: string;
    row_id: string;
    value: FieldValueContainer;
    display_text: string | null;
    client_field_id: string | null;
  };
  meta?: { version: number } | null;
};

export type FieldLookupTableRow = {
  type: string;
  id: string;
  attributes: {
    position: number;
    external_key: string | null;
    created_at: string | null;
    updated_at: string | null;
    deleted_at: string | null;
  };
  meta?: { version: number } | null;
};

type ListLookupTableRowsApiArg = {
  'filter[field_lookup_table_id]'?: string;
  'page[offset]'?: number;
  'page[limit]'?: number;
  sort?: string;
  'filter[row_id]'?: string;
  'fields[cells]'?: string;
  'filter[reference_value]'?: string;
};

export type ListLookupTableRowsApiResponse = {
  data: FieldLookupTableRow[];
  included?: FieldLookupTableCell[] | null;
  meta: {
    page: { offset: number; limit: number; total: number };
  };
};

type GetLookupTableRowApiArg = {
  row_id: string;
  'fields[cells]'?: string;
};

type GetLookupTableRowApiResponse = {
  data: FieldLookupTableRow;
  included?: FieldLookupTableCell[] | null;
};

export type LookupTableCellInput = {
  column_key?: string | null;
  column_id?: string | null;
  value: FieldValueContainer;
};

type CreateLookupTableRowItem = {
  type: 'field_lookup_table_rows';
  attributes: {
    cells?: LookupTableCellInput[];
    external_key?: string | null;
  };
};

type CreateLookupTableRowsApiArg = {
  field_lookup_table_id: string;
  data: CreateLookupTableRowItem[];
};

type CreateLookupTableRowsApiResponse = ListLookupTableRowsApiResponse;

type UpdateLookupTableRowApiArg = {
  row_id: string;
  data: {
    type: 'field_lookup_table_rows';
    attributes: {
      position?: number | null;
      cells?: Record<string, FieldValueContainer | null> | null;
    };
    meta: { version: number };
  };
};

type UpdateLookupTableRowApiResponse = {
  data: FieldLookupTableRow;
  included?: FieldLookupTableCell[] | null;
};

type DeleteLookupTableRowApiArg = { row_id: string };

type ReorderLookupTableRowsApiArg = {
  field_lookup_table_id: string;
  data:
    | {
        type: 'field_lookup_table_row_moves';
        attributes: { row_id: string; before_row_id?: string | null };
        meta: { version: number };
      }
    | {
        type: 'field_lookup_table_row_orders';
        attributes: { ordered_row_ids: string[] };
        meta: { version: number };
      };
};

type BulkUpsertLookupTableRowItem = {
  type: 'field_lookup_table_rows';
  id?: string | null;
  attributes: {
    cells: LookupTableCellInput[];
    external_key?: string | null;
  };
};

type BulkUpsertLookupTableRowsApiArg = {
  field_lookup_table_id: string;
  data: BulkUpsertLookupTableRowItem[];
};

type SkippedLookupTableRow = { index: number; reason: string };

type BulkUpsertLookupTableRowsApiResponse = {
  data: FieldLookupTableRow[];
  included?: FieldLookupTableCell[] | null;
  meta: {
    page: { offset: number; limit: number; total: number };
    skipped: SkippedLookupTableRow[];
  };
};

// --- Field Lookup Table Cells ---

type GetLookupTableCellApiArg = { cell_id: string };

type GetLookupTableCellApiResponse = { data: FieldLookupTableCell };

type UpdateLookupTableCellApiArg = {
  cell_id: string;
  data: {
    type: 'field_lookup_table_cells';
    attributes: { value: FieldValueContainer };
    meta: { version: number };
  };
};

type UpdateLookupTableCellApiResponse = { data: FieldLookupTableCell };

type ClearLookupTableCellApiArg = { cell_id: string };

// --- Endpoints ---

// Matches the single-arg shape RTK Query actually passes into `queryFn`'s
// `baseQuery` parameter (see `queryFn` in RTK's `endpointDefinitions.ts`) —
// not the 3-arg `BaseQueryFn` type itself.
type FieldsBaseQuery = (
  arg: UnifiedFieldsBaseQueryArg,
) => ReturnType<BaseQueryFn<UnifiedFieldsBaseQueryArg, unknown, unknown>>;

// Shared by listAllActiveFields and listAllFields: reads the first page's
// `meta.page.total`, then pulls whatever pages remain in parallel. `extraParams`
// carries the one param that differs between the two (`filter[active]`).
const fetchAllFieldPages = async (
  baseQuery: FieldsBaseQuery,
  extraParams: Record<string, unknown>,
) => {
  const firstPage = await baseQuery({
    url: '/api/v1/client_fields',
    params: {
      'page[offset]': 0,
      'page[limit]': FIELDS_PAGE_LIMIT,
      ...extraParams,
    },
  });
  if (firstPage.error) return { error: firstPage.error };

  const firstData = firstPage.data as ListFieldsApiResponse;
  const { total } = firstData.meta.page;
  const remainingPages = Math.ceil(total / FIELDS_PAGE_LIMIT) - 1;

  const restResults = await Promise.all(
    Array.from({ length: Math.max(remainingPages, 0) }, (_page, index) =>
      baseQuery({
        url: '/api/v1/client_fields',
        params: {
          'page[offset]': FIELDS_PAGE_LIMIT * (index + 1),
          'page[limit]': FIELDS_PAGE_LIMIT,
          ...extraParams,
        },
      }),
    ),
  );
  const failed = restResults.find((result) => result.error);
  if (failed?.error) return { error: failed.error };

  const pages = [
    firstData,
    ...restResults.map((result) => result.data as ListFieldsApiResponse),
  ];
  const fields = pages.flatMap((page) => page.data);

  // Field sections repeat identically across every page's `included` — dedupe
  // by id rather than concatenating them.
  const includedById = new Map<string, FieldSection>();
  for (const page of pages) {
    for (const section of page.included ?? []) {
      includedById.set(section.id, section);
    }
  }
  const included = Array.from(includedById.values());

  return {
    data: {
      data: fields,
      included: included.length > 0 ? included : undefined,
      meta: { page: { offset: 0, limit: fields.length, total } },
    },
  };
};

/**
 * @lintignore Not imported by name anywhere, but this export must exist: dropping it
 * (or knip's fix un-exporting/deleting it) risks the module never being evaluated, which
 * would mean `injectEndpoints` never registers these endpoints on the shared root api.
 */
export const injected = unifiedFieldsApiRoot.injectEndpoints({
  endpoints: (build) => ({
    listFields: build.query<ListFieldsApiResponse, ListFieldsApiArg>({
      query: (queryArg) => ({
        url: '/api/v1/client_fields',
        params: {
          'page[offset]': queryArg['page[offset]'],
          'page[limit]': queryArg['page[limit]'],
          'filter[id]': queryArg['filter[id]'],
          'filter[api_name]': queryArg['filter[api_name]'],
          'filter[active]': queryArg['filter[active]'],
          'filter[search_query]': queryArg['filter[search_query]'],
          sort: queryArg.sort,
          include: queryArg.include,
        },
      }),
      // Mirrors listAllFields: `include=field_section` means a field-section
      // rename/reorder/delete also needs to refetch this, not just a
      // field-membership change.
      providesTags: (_result, _error, arg) =>
        arg.include === 'field_section'
          ? [
              ...clientFieldListTags,
              { type: AutogenTags.FieldSection, id: 'LIST' },
            ]
          : clientFieldListTags,
    }),
    createFields: build.mutation<CreateFieldsApiResponse, CreateFieldsApiArg>({
      query: (body) => ({
        url: '/api/v1/client_fields',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: TagType.UnifiedField, id: 'LIST' }],
    }),
    updateField: build.mutation<UpdateFieldApiResponse, UpdateFieldApiArg>({
      query: ({ field_id, ...body }) => ({
        url: `/api/v1/client_fields/${field_id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { field_id }) => [
        { type: TagType.UnifiedField, id: field_id },
        { type: TagType.UnifiedField, id: 'LIST' },
      ],
    }),
    validateFields: build.mutation<
      ValidateFieldsApiResponse,
      ValidateFieldsApiArg
    >({
      query: (body) => ({
        url: '/api/v1/client_fields/validate',
        method: 'POST',
        body,
      }),
    }),
    createOptionSet: build.mutation<
      CreateOptionSetApiResponse,
      CreateOptionSetApiArg
    >({
      query: (body) => ({
        url: '/api/v1/option_sets',
        method: 'POST',
        body,
      }),
    }),
    deleteOptionSet: build.mutation<void, DeleteOptionSetApiArg>({
      query: ({ option_set_id }) => ({
        url: `/api/v1/option_sets/${option_set_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: TagType.UnifiedField, id: 'LIST' }],
    }),
    listFieldOptions: build.query<
      ListFieldOptionsApiResponse,
      ListFieldOptionsApiArg
    >({
      query: (queryArg) => ({
        url: '/api/v1/field_options',
        params: {
          'page[offset]': queryArg['page[offset]'],
          'page[limit]': queryArg['page[limit]'],
          'filter[option_set_id]': queryArg['filter[option_set_id]'],
          'filter[search]': queryArg['filter[search]'],
        },
      }),
      providesTags: (_result, _error, { 'filter[option_set_id]': id }) =>
        id
          ? [{ type: TagType.UnifiedField, id: `FIELD_OPTIONS_${id}` }]
          : [{ type: TagType.UnifiedField, id: 'FIELD_OPTIONS_LIST' }],
    }),
    createFieldOptions: build.mutation<
      CreateFieldOptionsApiResponse,
      CreateFieldOptionsApiArg
    >({
      query: ({ data }) => ({
        url: '/api/v1/field_options',
        method: 'POST',
        body: { data },
      }),
      invalidatesTags: (_result, _error, { data, skipInvalidation }) => {
        if (skipInvalidation) return [];
        const optionSetId = data[0]?.attributes.option_set_id;
        return optionSetId
          ? [{ type: TagType.UnifiedField, id: `FIELD_OPTIONS_${optionSetId}` }]
          : [{ type: TagType.UnifiedField, id: 'FIELD_OPTIONS_LIST' }];
      },
    }),
    deleteFieldOptions: build.mutation<void, DeleteFieldOptionsApiArg>({
      query: ({ ids }) => ({
        url: '/api/v1/field_options',
        method: 'DELETE',
        body: { ids },
      }),
      invalidatesTags: (_result, _error, { option_set_id }) => [
        { type: TagType.UnifiedField, id: `FIELD_OPTIONS_${option_set_id}` },
      ],
    }),
    updateFieldOption: build.mutation<
      UpdateFieldOptionApiResponse,
      UpdateFieldOptionApiArg
    >({
      query: ({ option_id, data }) => ({
        url: `/api/v1/field_options/${option_id}`,
        method: 'PATCH',
        body: { data },
      }),
      invalidatesTags: (_result, _error, { option_set_id }) => [
        { type: TagType.UnifiedField, id: `FIELD_OPTIONS_${option_set_id}` },
      ],
    }),
    listFieldsByIds: build.query<ListFieldsApiResponse, string[]>({
      queryFn: async (fieldIds, _api, _extraOptions, baseQuery) => {
        const uniqueFieldIds = [...new Set(fieldIds)];
        const results = await Promise.all(
          chunk(uniqueFieldIds, BATCH_SIZE).map((batch) =>
            baseQuery({
              url: '/api/v1/client_fields',
              params: {
                'filter[id]': batch.join(','),
                'page[limit]': BATCH_SIZE,
              },
            }),
          ),
        );
        const failed = results.find((result) => result.error);
        if (failed?.error) return { error: failed.error };

        const fields = results.flatMap(
          (result) => (result.data as ListFieldsApiResponse).data,
        );
        return {
          data: {
            data: fields,
            meta: {
              page: { offset: 0, limit: fields.length, total: fields.length },
            },
          },
        };
      },
      providesTags: (_result, _error, fieldIds) => [
        ...fieldIds.map((id) => ({ type: TagType.UnifiedField, id }) as const),
        { type: AutogenTags.ClientField, id: 'LIST' } as const,
      ],
    }),
    // Chunk filter[api_name] lookups so page[limit] never exceeds the server
    // cap of 100 (callers previously used Math.max(names.length, …) and 422'd).
    listFieldsByApiNames: build.query<ListFieldsApiResponse, string[]>({
      queryFn: async (apiNames, _api, _extraOptions, baseQuery) => {
        const uniqueApiNames = [...new Set(apiNames)];
        const results = await Promise.all(
          chunk(uniqueApiNames, BATCH_SIZE).map((batch) =>
            baseQuery({
              url: '/api/v1/client_fields',
              params: {
                'filter[active]': true,
                'filter[api_name]': batch.join(','),
                'page[limit]': BATCH_SIZE,
              },
            }),
          ),
        );
        const failed = results.find((result) => result.error);
        if (failed?.error) return { error: failed.error };

        const fields = results.flatMap(
          (result) => (result.data as ListFieldsApiResponse).data,
        );
        return {
          data: {
            data: fields,
            meta: {
              page: { offset: 0, limit: fields.length, total: fields.length },
            },
          },
        };
      },
      providesTags: () => clientFieldListTags,
    }),
    listAllActiveFields: build.query<ListFieldsApiResponse, void>({
      // `filter[active]` keeps deactivated fields off the wire, so `total`
      // (and the page count) covers active fields only.
      queryFn: (_arg, _api, _extraOptions, baseQuery) =>
        fetchAllFieldPages(baseQuery, { 'filter[active]': true }),
      providesTags: () => clientFieldListTags,
    }),
    // Every field regardless of active status — unlike listAllActiveFields,
    // callers that need to recognize a deactivated field (not just omit it)
    // require it to actually be present in the data, `deactivated_at` set.
    // `include=field_section` embeds each field's section in `included`, so
    // this also needs to refetch on a field-section rename/reorder/delete,
    // not just a field-membership change.
    listAllFields: build.query<
      ListFieldsApiResponse,
      { contractTypes?: string[] } | void
    >({
      queryFn: (arg, _api, _extraOptions, baseQuery) =>
        fetchAllFieldPages(baseQuery, {
          include: 'field_section',
          ...(arg?.contractTypes?.length
            ? { 'filter[contract_type]': arg.contractTypes.join(',') }
            : {}),
        }),
      providesTags: () => [
        ...clientFieldListTags,
        { type: AutogenTags.FieldSection, id: 'LIST' },
      ],
    }),

    // --- Field Lookup Tables ---
    listLookupTables: build.query<
      ListLookupTablesApiResponse,
      ListLookupTablesApiArg
    >({
      query: (queryArg) => ({
        url: '/api/v1/field_lookup_tables',
        params: {
          'page[offset]': queryArg['page[offset]'],
          'page[limit]': queryArg['page[limit]'],
          'filter[id]': queryArg['filter[id]'],
          'filter[active]': queryArg['filter[active]'],
          'filter[name]': queryArg['filter[name]'],
          'filter[reference_field_id]': queryArg['filter[reference_field_id]'],
          include_columns: queryArg.include_columns,
        },
      }),
      providesTags: () => [
        { type: TagType.UnifiedField, id: 'LOOKUP_TABLE_LIST' },
      ],
    }),
    getLookupTable: build.query<
      GetLookupTableApiResponse,
      GetLookupTableApiArg
    >({
      query: ({ table_id }) => ({
        url: `/api/v1/field_lookup_tables/${table_id}`,
      }),
      providesTags: (_result, _error, { table_id }) => [
        { type: TagType.UnifiedField, id: table_id },
      ],
    }),
    createLookupTable: build.mutation<
      CreateLookupTableApiResponse,
      CreateLookupTableApiArg
    >({
      query: (body) => ({
        url: '/api/v1/field_lookup_tables',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: TagType.UnifiedField, id: 'LOOKUP_TABLE_LIST' },
      ],
    }),
    updateLookupTable: build.mutation<
      UpdateLookupTableApiResponse,
      UpdateLookupTableApiArg
    >({
      query: ({ table_id, ...body }) => ({
        url: `/api/v1/field_lookup_tables/${table_id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { table_id }) => [
        { type: TagType.UnifiedField, id: table_id },
        { type: TagType.UnifiedField, id: 'LOOKUP_TABLE_LIST' },
      ],
    }),
    deleteLookupTable: build.mutation<void, DeleteLookupTableApiArg>({
      query: ({ table_id }) => ({
        url: `/api/v1/field_lookup_tables/${table_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { table_id }) => [
        { type: TagType.UnifiedField, id: table_id },
        { type: TagType.UnifiedField, id: 'LOOKUP_TABLE_LIST' },
      ],
    }),

    // --- Field Lookup Table Columns ---
    listLookupTableColumns: build.query<
      ListLookupTableColumnsApiResponse,
      ListLookupTableColumnsApiArg
    >({
      query: (queryArg) => ({
        url: '/api/v1/field_lookup_table_columns',
        params: {
          'filter[column_id]': queryArg['filter[column_id]'],
          'filter[field_lookup_table_id]':
            queryArg['filter[field_lookup_table_id]'],
          'page[offset]': queryArg['page[offset]'],
          'page[limit]': queryArg['page[limit]'],
          include_client_field: queryArg.include_client_field,
        },
      }),
      providesTags: () => [
        { type: TagType.UnifiedField, id: 'LOOKUP_TABLE_COLUMN_LIST' },
      ],
    }),
    getLookupTableColumn: build.query<
      GetLookupTableColumnApiResponse,
      GetLookupTableColumnApiArg
    >({
      query: ({ column_id, include_client_field }) => ({
        url: `/api/v1/field_lookup_table_columns/${column_id}`,
        params: { include_client_field },
      }),
      providesTags: (_result, _error, { column_id }) => [
        { type: TagType.UnifiedField, id: column_id },
      ],
    }),
    createLookupTableColumns: build.mutation<
      CreateLookupTableColumnsApiResponse,
      CreateLookupTableColumnsApiArg
    >({
      query: (body) => ({
        url: '/api/v1/field_lookup_table_columns',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: TagType.UnifiedField, id: 'LOOKUP_TABLE_COLUMN_LIST' },
        { type: TagType.UnifiedField, id: 'LOOKUP_TABLE_LIST' },
      ],
    }),
    updateLookupTableColumn: build.mutation<
      UpdateLookupTableColumnApiResponse,
      UpdateLookupTableColumnApiArg
    >({
      query: ({ column_id, ...body }) => ({
        url: `/api/v1/field_lookup_table_columns/${column_id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { column_id }) => [
        { type: TagType.UnifiedField, id: column_id },
        { type: TagType.UnifiedField, id: 'LOOKUP_TABLE_COLUMN_LIST' },
        { type: TagType.UnifiedField, id: 'LOOKUP_TABLE_LIST' },
      ],
    }),
    deleteLookupTableColumn: build.mutation<
      void,
      DeleteLookupTableColumnApiArg
    >({
      query: ({ column_id }) => ({
        url: `/api/v1/field_lookup_table_columns/${column_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { column_id }) => [
        { type: TagType.UnifiedField, id: column_id },
        { type: TagType.UnifiedField, id: 'LOOKUP_TABLE_COLUMN_LIST' },
        { type: TagType.UnifiedField, id: 'LOOKUP_TABLE_ROW_LIST' },
        { type: TagType.UnifiedField, id: 'LOOKUP_TABLE_LIST' },
      ],
    }),
    reorderLookupTableColumns: build.mutation<
      void,
      ReorderLookupTableColumnsApiArg
    >({
      query: ({ field_lookup_table_id, data }) => ({
        url: '/api/v1/field_lookup_table_columns/reorder',
        method: 'POST',
        params: { field_lookup_table_id },
        body: { data },
      }),
      invalidatesTags: [
        { type: TagType.UnifiedField, id: 'LOOKUP_TABLE_COLUMN_LIST' },
        { type: TagType.UnifiedField, id: 'LOOKUP_TABLE_LIST' },
      ],
    }),

    // --- Field Lookup Table Rows ---
    listLookupTableRows: build.query<
      ListLookupTableRowsApiResponse,
      ListLookupTableRowsApiArg
    >({
      query: (queryArg) => ({
        url: '/api/v1/field_lookup_table_rows',
        params: {
          'filter[field_lookup_table_id]':
            queryArg['filter[field_lookup_table_id]'],
          'page[offset]': queryArg['page[offset]'],
          'page[limit]': queryArg['page[limit]'],
          sort: queryArg.sort,
          'filter[row_id]': queryArg['filter[row_id]'],
          'fields[cells]': queryArg['fields[cells]'],
          'filter[reference_value]': queryArg['filter[reference_value]'],
        },
      }),
      providesTags: () => [
        { type: TagType.UnifiedField, id: 'LOOKUP_TABLE_ROW_LIST' },
      ],
    }),
    getLookupTableRow: build.query<
      GetLookupTableRowApiResponse,
      GetLookupTableRowApiArg
    >({
      query: ({ row_id, ...params }) => ({
        url: `/api/v1/field_lookup_table_rows/${row_id}`,
        params,
      }),
      providesTags: (_result, _error, { row_id }) => [
        { type: TagType.UnifiedField, id: row_id },
      ],
    }),
    createLookupTableRows: build.mutation<
      CreateLookupTableRowsApiResponse,
      CreateLookupTableRowsApiArg
    >({
      query: ({ field_lookup_table_id, data }) => ({
        url: '/api/v1/field_lookup_table_rows',
        method: 'POST',
        params: { field_lookup_table_id },
        body: { data },
      }),
      invalidatesTags: [
        { type: TagType.UnifiedField, id: 'LOOKUP_TABLE_ROW_LIST' },
      ],
    }),
    updateLookupTableRow: build.mutation<
      UpdateLookupTableRowApiResponse,
      UpdateLookupTableRowApiArg
    >({
      query: ({ row_id, ...body }) => ({
        url: `/api/v1/field_lookup_table_rows/${row_id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { row_id }) => [
        { type: TagType.UnifiedField, id: row_id },
        { type: TagType.UnifiedField, id: 'LOOKUP_TABLE_ROW_LIST' },
      ],
    }),
    deleteLookupTableRow: build.mutation<void, DeleteLookupTableRowApiArg>({
      query: ({ row_id }) => ({
        url: `/api/v1/field_lookup_table_rows/${row_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { row_id }) => [
        { type: TagType.UnifiedField, id: row_id },
        { type: TagType.UnifiedField, id: 'LOOKUP_TABLE_ROW_LIST' },
      ],
    }),
    reorderLookupTableRows: build.mutation<void, ReorderLookupTableRowsApiArg>({
      query: ({ field_lookup_table_id, data }) => ({
        url: '/api/v1/field_lookup_table_rows/reorder',
        method: 'POST',
        params: { field_lookup_table_id },
        body: { data },
      }),
      invalidatesTags: [
        { type: TagType.UnifiedField, id: 'LOOKUP_TABLE_ROW_LIST' },
        { type: TagType.UnifiedField, id: 'LOOKUP_TABLE_LIST' },
      ],
    }),
    bulkUpsertLookupTableRows: build.mutation<
      BulkUpsertLookupTableRowsApiResponse,
      BulkUpsertLookupTableRowsApiArg
    >({
      query: ({ field_lookup_table_id, data }) => ({
        url: '/api/v1/field_lookup_table_rows/bulk-upsert',
        method: 'POST',
        params: { field_lookup_table_id },
        body: { data },
      }),
      invalidatesTags: [
        { type: TagType.UnifiedField, id: 'LOOKUP_TABLE_ROW_LIST' },
      ],
    }),

    // --- Field Lookup Table Cells ---
    getLookupTableCell: build.query<
      GetLookupTableCellApiResponse,
      GetLookupTableCellApiArg
    >({
      query: ({ cell_id }) => ({
        url: `/api/v1/field_lookup_table_cells/${cell_id}`,
      }),
      providesTags: (_result, _error, { cell_id }) => [
        { type: TagType.UnifiedField, id: cell_id },
      ],
    }),
    updateLookupTableCell: build.mutation<
      UpdateLookupTableCellApiResponse,
      UpdateLookupTableCellApiArg
    >({
      query: ({ cell_id, ...body }) => ({
        url: `/api/v1/field_lookup_table_cells/${cell_id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: [
        { type: TagType.UnifiedField, id: 'LOOKUP_TABLE_ROW_LIST' },
      ],
    }),
    clearLookupTableCell: build.mutation<void, ClearLookupTableCellApiArg>({
      query: ({ cell_id }) => ({
        url: `/api/v1/field_lookup_table_cells/${cell_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        { type: TagType.UnifiedField, id: 'LOOKUP_TABLE_ROW_LIST' },
      ],
    }),
  }),
  // `setup.ts` imports the codegen'd `typedefs.autogen` first, which registers
  // every endpoint the OpenAPI schema describes (including ones with the same
  // names as the hand-written ones below, e.g. `listFields`, `getLookupTable`,
  // but with different, nested-object argument shapes). `overrideExisting:
  // true` lets these hand-written definitions win back control of those
  // names — and correctly rebind their hooks — so every existing call site
  // keeps working against the shapes it already expects. Net-new endpoints
  // not defined here (e.g. field sections) are left on the generated
  // definitions untouched.
  overrideExisting: true,
});

export const {
  useListFieldsQuery,
  useListFieldsByIdsQuery,
  useListFieldsByApiNamesQuery,
  useListAllActiveFieldsQuery,
  useListAllFieldsQuery,
} = injected;
